import React from 'react';
import { format, parseISO } from 'date-fns';
import { Clock, ExternalLink, Trash2, Link as LinkIcon, Copy } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { cn } from '../../lib/utils';
import { FILE_TYPE_CONFIG, inferProvider } from './fileConstants';
import { useDeleteFile } from '../../hooks/useDigitalFiles';
import { toast } from 'sonner';

export default function FileDetailSheet({ file, open, onClose, isReadOnly }) {
  const deleteMutation = useDeleteFile();

  if (!file) return null;

  const cfg = FILE_TYPE_CONFIG[file.file_type] || FILE_TYPE_CONFIG.other;
  const provider = inferProvider(file.external_url);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(file.id);
      toast.success('File link removed');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleOpen = () => {
    if (!file.external_url) return;
    window.open(file.external_url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(file.external_url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="file-detail-sheet">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={cn('flex items-center justify-center rounded-lg h-8 w-8 text-base', cfg.light)}>{cfg.icon}</div>
            <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider', cfg.light)}>{cfg.label}</Badge>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-muted-foreground/30">
              {provider.label}
            </Badge>
          </div>
          <SheetTitle className="text-xl font-display">{file.name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-6">
          {file.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">{file.description}</p>
          )}

          <div className="flex items-start gap-3">
            <LinkIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-sm min-w-0 flex-1">
              <p className="text-xs text-muted-foreground mb-0.5">External URL</p>
              <p className="text-xs font-mono break-all text-foreground/80">{file.external_url}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>Added {format(parseISO(file.created_at), 'MMM d, yyyy h:mm a')}</p>
              {file.updated_at !== file.created_at && (
                <p>Updated {format(parseISO(file.updated_at), 'MMM d, yyyy h:mm a')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 border-t pt-4">
          <Button
            onClick={handleOpen}
            className="flex-1 gap-2"
            data-testid="file-detail-open"
          >
            <ExternalLink className="h-4 w-4" /> Open Link
          </Button>
          <Button
            onClick={handleCopy}
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="Copy link"
            data-testid="file-detail-copy"
          >
            <Copy className="h-4 w-4" />
          </Button>
          {!isReadOnly && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon" className="shrink-0" data-testid="file-detail-delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove File Link</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove the link to &ldquo;{file.name}&rdquo; from FrameFlow. The original file on {provider.label} stays untouched.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="file-detail-delete-confirm"
                  >
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
